import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DialogHeader } from "../DialogHeader/DialogHeader";
import { useMember } from "../../context/UserContext";
import { useSnackbar } from "../../context/SnackbarContext";
import { supabase } from "../../services/supabaseClient";

const E164_REGEX = /^\+[1-9]\d{1,14}$/;

export default function RCSMessageDialog() {
  const { member } = useMember();
  const { showAlert } = useSnackbar();
  const [open, setOpen] = useState(true);
  const [phone, setPhone] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const chapterId = member?.chapters?.chapter_id;
  const shouldShow = import.meta.env.PROD && chapterId === 75;

  if (!shouldShow) return null;

  const handlePhoneChange = (value: string) => {
    setPhone(value);
    if (value && !E164_REGEX.test(value)) {
      setPhoneError("Enter a valid E.164 number (e.g. +15551234567)");
    } else {
      setPhoneError("");
    }
  };

  const handleSend = async () => {
    if (!E164_REGEX.test(phone)) {
      setPhoneError("Enter a valid E.164 number (e.g. +15551234567)");
      return;
    }
    if (!body.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke("send-rcs-message", {
        body: { phone, body: body.trim() },
      });
      if (error) throw error;
      showAlert("RCS message sent successfully", "success");
      setPhone("");
      setBody("");
      setOpen(false);
    } catch (err) {
      console.error("Failed to send RCS message:", err);
      showAlert("Failed to send message. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = E164_REGEX.test(phone) && body.trim().length > 0 && !loading;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
      <DialogHeader title="Send RCS Message" onClose={() => setOpen(false)} disabled={loading} />
      <DialogContent sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          Send an RCS message to a phone number. Enter the number in E.164 format (e.g.{" "}
          <strong>+15551234567</strong>).
        </Typography>
        <TextField
          label="Phone Number"
          placeholder="+15551234567"
          value={phone}
          onChange={(e) => handlePhoneChange(e.target.value)}
          error={!!phoneError}
          helperText={phoneError}
          fullWidth
          variant="outlined"
          size="small"
          disabled={loading}
        />
        <TextField
          label="Message"
          placeholder="Type your message..."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          variant="outlined"
          disabled={loading}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setOpen(false)} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSend} disabled={!canSubmit}>
          {loading ? <CircularProgress size={22} color="inherit" /> : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
